// src/components/Navbar.js
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link, useLocation } from 'react-router-dom';
import logoImage from '../assets/logo.png';

/* ===============================
   NAVBAR STYLES — Light Theme
================================*/

const Nav = styled.nav`
  position: sticky;
  top: 0;
  z-index: 2000;

  display: flex;
  justify-content: space-between;
  align-items: center;

  padding: ${props => (props.scrolled ? '0.6rem 10%' : '1rem 10%')};
  transition: all 0.25s ease;

  background: ${props =>
    props.scrolled
      ? 'rgba(255, 255, 255, 0.95)'
      : 'rgba(255, 255, 255, 0.92)'};
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);

  border-bottom: 1px solid ${props =>
    props.scrolled ? 'var(--border-color)' : 'rgba(226, 232, 240, 0.7)'};
  box-shadow: ${props =>
    props.scrolled
      ? 'var(--shadow-md)'
      : 'none'};

  @media (max-width: 768px) {
    padding: 0.8rem 7%;
  }
`;

const LogoContainer = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  text-decoration: none;
`;

const LogoImage = styled.img`
  width: 38px;
  height: 38px;
  filter: drop-shadow(0 2px 8px rgba(41, 171, 226, 0.35));
`;

const LogoText = styled.span`
  font-size: 1.4rem;
  font-weight: 700;
  background: linear-gradient(135deg, #29ABE2, #F7941D);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  @media (max-width: 768px) {
    display: none;
  }
`;

const NavLinks = styled.div`
  display: flex;
  gap: 1.8rem;
  align-items: center;

  @media (max-width: 768px) {
    display: none;
  }
`;

const NavLinkStyled = styled(Link)`
  position: relative;
  color: ${props => (props.active ? 'var(--primary-color)' : 'var(--text-secondary-color)')};
  text-decoration: none;
  font-size: 0.9rem;
  font-weight: 500;
  transition: all 0.25s ease;

  &:hover {
    color: var(--primary-color);
  }

  &::after {
    content: '';
    position: absolute;
    left: 0;
    bottom: -4px;
    width: ${props => (props.active ? '100%' : '0%')};
    height: 2px;
    background: linear-gradient(90deg, var(--primary-color), var(--accent-color));
    border-radius: 10px;
    transition: width 0.3s ease;
  }

  &:hover::after {
    width: 100%;
  }
`;

const CTAButton = styled.a`
  background: linear-gradient(135deg, #29ABE2, #1A5276);
  padding: 0.6rem 1.4rem;
  border-radius: 10px;
  color: white;
  font-weight: 600;
  font-size: 0.9rem;
  text-decoration: none;
  box-shadow: var(--shadow-primary);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px) scale(1.03);
    box-shadow: var(--shadow-primary-lg);
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

/* ===============================
   HAMBURGER MENU
================================*/
const Hamburger = styled.div`
  width: 28px;
  height: 22px;
  position: relative;
  cursor: pointer;
  display: none;

  @media (max-width: 768px) {
    display: block;
  }

  div {
    position: absolute;
    height: 2.5px;
    width: 100%;
    background: var(--text-color);
    border-radius: 5px;
    transition: 0.4s ease;
  }

  div:nth-child(1) {
    top: ${props => (props.open ? '10px' : '0')};
    transform: ${props => (props.open ? 'rotate(45deg)' : 'none')};
  }

  div:nth-child(2) {
    opacity: ${props => (props.open ? '0' : '1')};
    top: 9px;
  }

  div:nth-child(3) {
    bottom: ${props => (props.open ? '9px' : '0')};
    transform: ${props => (props.open ? 'rotate(-45deg)' : 'none')};
  }
`;

/* ===============================
   MOBILE MENU DRAWER
================================*/
const MobileMenuWrapper = styled.div`
  position: fixed;
  top: 0;
  right: ${props => (props.open ? '0' : '-100%')};
  width: 78%;
  max-width: 340px;
  height: 100vh;
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  transition: 0.35s ease;
  padding: 2rem 1.5rem;
  z-index: 2500;
  box-shadow: -5px 0 40px rgba(15, 23, 42, 0.12);
  border-left: 1px solid var(--border-color);
`;

const MobileMenuClose = styled.button`
  position: absolute;
  top: 1.2rem;
  right: 1.2rem;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 1.1rem;
  color: var(--text-secondary-color);
  transition: all 0.2s ease;

  &:hover {
    background: var(--bg-tertiary);
    color: var(--text-color);
  }
`;

const MobileMenuLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin-top: 3rem;
`;

const MobileNavLink = styled(Link)`
  color: var(--text-secondary-color);
  font-size: 1.05rem;
  text-decoration: none;
  font-weight: 500;
  padding: 0.75rem 1rem;
  border-radius: 10px;
  transition: all 0.2s ease;

  &:hover {
    color: var(--primary-color);
    background: var(--primary-light);
  }
`;

const MobileCTA = styled.a`
  margin-top: 1.5rem;
  display: block;
  text-align: center;
  background: linear-gradient(135deg, #29ABE2, #1A5276);
  padding: 0.9rem 1.6rem;
  border-radius: 12px;
  color: white;
  font-weight: 600;
  text-decoration: none;
  font-size: 1rem;
  box-shadow: var(--shadow-primary);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: var(--shadow-primary-lg);
  }
`;

/* ===============================
   OVERLAY
================================*/
const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  width: 100vw;
  background: rgba(15, 23, 42, 0.35);
  backdrop-filter: blur(4px);
  opacity: ${props => (props.open ? '1' : '0')};
  pointer-events: ${props => (props.open ? 'auto' : 'none')};
  transition: opacity 0.3s ease;
  z-index: 2000;
`;

/* ===============================
   MAIN COMPONENT
================================*/
const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const links = [
    { name: 'Home', to: '/' },
    { name: 'About Us', to: '/about' },
    { name: 'Genie Social', to: '/pricing' },
    { name: 'Rewards', to: '/rewards' },
    { name: '$TGEN Token', to: '/token' },
    { name: 'Live Results', to: '/live-results' },
    { name: 'Insights', to: '/insights' },
    { name: 'White Paper', to: '/whitepaper' },
  ];

  return (
    <>
      <Nav scrolled={scrolled}>
        <LogoContainer to="/">
          <LogoImage src={logoImage} alt="Trend Genie — Best AI Crypto Signals App" />
          <LogoText>Trend Genie</LogoText>
        </LogoContainer>

        {/* Desktop Links */}
        <NavLinks>
          {links.map((link, i) => (
            <NavLinkStyled
              key={i}
              to={link.to}
              active={location.pathname === link.to ? 1 : 0}
            >
              {link.name}
            </NavLinkStyled>
          ))}
        </NavLinks>

        <CTAButton
          href="https://social.trendgenie.io"
          target="_blank"
          rel="noopener noreferrer"
        >
          Genie Social
        </CTAButton>

        {/* Hamburger */}
        <Hamburger open={open} onClick={() => setOpen(!open)}>
          <div></div>
          <div></div>
          <div></div>
        </Hamburger>
      </Nav>

      {/* Mobile Overlay */}
      <Overlay open={open} onClick={() => setOpen(false)} />

      {/* Mobile Drawer */}
      <MobileMenuWrapper open={open}>
        <MobileMenuClose onClick={() => setOpen(false)}>✕</MobileMenuClose>

        <LogoContainer to="/" style={{ textDecoration: 'none' }}>
          <LogoImage src={logoImage} alt="Trend Genie" />
          <LogoText style={{ display: 'block' }}>Trend Genie</LogoText>
        </LogoContainer>

        <MobileMenuLinks>
          {links.map((link, i) => (
            <MobileNavLink key={i} to={link.to}>
              {link.name}
            </MobileNavLink>
          ))}
        </MobileMenuLinks>

        <MobileCTA
          href="https://play.google.com/store/apps/details?id=com.entiresolutions.trendgenie"
          target="_blank"
          rel="noopener noreferrer"
        >
          Download Free App
        </MobileCTA>
      </MobileMenuWrapper>
    </>
  );
};

export default Navbar;
